import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import AppTextInput from './AppTextInput';
import { VehicleProfile, formatVehicleLabel } from '../types/vehicle';
import { vehicleGarageService } from '../services/vehicleGarageService';

interface MyVehiclesModalProps {
  visible: boolean;
  userId: string;
  selectedVehicleId?: string | null;
  onSelectVehicleId: (vehicleId: string | null) => void;
  onClose: () => void;
}

type Draft = {
  id?: string;
  nickname?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  vin?: string;
  plate?: string;
  notes?: string;
  isDefault?: boolean;
};

const emptyDraft = (): Draft => ({
  nickname: '',
  year: '',
  make: '',
  model: '',
  trim: '',
  vin: '',
  plate: '',
  notes: '',
  isDefault: false,
});

const MyVehiclesModal: React.FC<MyVehiclesModalProps> = ({
  visible,
  userId,
  selectedVehicleId,
  onSelectVehicleId,
  onClose,
}) => {
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  const selectedVehicle = useMemo(
    () => vehicles.find(v => v.id === selectedVehicleId) || null,
    [vehicles, selectedVehicleId]
  );

  const reload = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const v = await vehicleGarageService.listVehicles(userId);
      setVehicles(v);
    } catch (e) {
      console.warn('MyVehiclesModal: load error', (e as any)?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      reload();
      setIsEditing(false);
      setDraft(emptyDraft());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userId]);

  const startAdd = () => {
    setDraft(emptyDraft());
    setIsEditing(true);
  };

  const startEdit = (v: VehicleProfile) => {
    setDraft({
      id: v.id,
      nickname: v.nickname || '',
      year: v.year || '',
      make: v.make || '',
      model: v.model || '',
      trim: v.trim || '',
      vin: v.vin || '',
      plate: v.plate || '',
      notes: v.notes || '',
      isDefault: !!v.isDefault,
    });
    setIsEditing(true);
  };

  const save = async () => {
    if (!userId) return;
    const anyCore =
      (draft.nickname || '').trim() ||
      (draft.year || '').trim() ||
      (draft.make || '').trim() ||
      (draft.model || '').trim() ||
      (draft.trim || '').trim() ||
      (draft.vin || '').trim() ||
      (draft.plate || '').trim();

    if (!anyCore) {
      const msg = 'Please enter at least a nickname or a Year/Make/Model (or VIN/Plate).';
      if (Platform.OS === 'web') (window as any)?.alert?.(msg);
      else Alert.alert('Missing details', msg);
      return;
    }

    setLoading(true);
    try {
      const saved = await vehicleGarageService.upsertVehicle(userId, {
        id: draft.id,
        userId,
        nickname: (draft.nickname || '').trim() || undefined,
        year: (draft.year || '').trim() || undefined,
        make: (draft.make || '').trim() || undefined,
        model: (draft.model || '').trim() || undefined,
        trim: (draft.trim || '').trim() || undefined,
        vin: (draft.vin || '').trim() || undefined,
        plate: (draft.plate || '').trim() || undefined,
        notes: (draft.notes || '').trim() || undefined,
        isDefault: !!draft.isDefault,
      } as any);

      await reload();
      setIsEditing(false);
      setDraft(emptyDraft());

      // Auto-select what they just saved (good UX for booking)
      onSelectVehicleId(saved.id);
    } catch (e) {
      console.error('MyVehiclesModal: save error', e);
      const msg = 'Could not save vehicle. Please try again.';
      if (Platform.OS === 'web') (window as any)?.alert?.(msg);
      else Alert.alert('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (vehicleId: string) => {
    if (!userId) return;

    const doDelete = async () => {
      setLoading(true);
      try {
        await vehicleGarageService.deleteVehicle(userId, vehicleId);
        if (selectedVehicleId === vehicleId) onSelectVehicleId(null);
        await reload();
      } finally {
        setLoading(false);
      }
    };

    if (Platform.OS === 'web') {
      const ok = (window as any)?.confirm?.('Delete this vehicle?');
      if (ok) await doDelete();
      return;
    }

    Alert.alert('Delete vehicle?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => void doDelete() },
    ]);
  };

  const setDefault = async (vehicleId: string) => {
    if (!userId) return;
    setLoading(true);
    try {
      await vehicleGarageService.setDefaultVehicle(userId, vehicleId);
      await reload();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>My Vehicles</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Text style={styles.closeText}>✕</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.subheader}>
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => onSelectVehicleId(null)} disabled={loading}>
              <Text style={styles.secondaryBtnText}>No vehicle</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.primaryBtn} onPress={startAdd} disabled={loading}>
              <Text style={styles.primaryBtnText}>+ Add vehicle</Text>
            </TouchableOpacity>
          </View>

          {isEditing ? (
            <ScrollView style={styles.form} contentContainerStyle={{ paddingBottom: 16 }}>
              <Text style={styles.formTitle}>{draft.id ? 'Edit vehicle' : 'Add vehicle'}</Text>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Nickname (optional)</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.nickname}
                    onChangeText={t => setDraft(p => ({ ...p, nickname: t }))}
                    placeholder="e.g., My Camry"
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Year</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.year}
                    onChangeText={t => setDraft(p => ({ ...p, year: t }))}
                    placeholder="e.g., 2018"
                    keyboardType="number-pad"
                  />
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Make</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.make}
                    onChangeText={t => setDraft(p => ({ ...p, make: t }))}
                    placeholder="e.g., Toyota"
                    autoCapitalize="words"
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Model</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.model}
                    onChangeText={t => setDraft(p => ({ ...p, model: t }))}
                    placeholder="e.g., Camry"
                    autoCapitalize="words"
                  />
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Trim</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.trim}
                    onChangeText={t => setDraft(p => ({ ...p, trim: t }))}
                    placeholder="e.g., SE"
                    autoCapitalize="characters"
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>License plate (optional)</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.plate}
                    onChangeText={t => setDraft(p => ({ ...p, plate: t }))}
                    placeholder="e.g., ABC123"
                    autoCapitalize="characters"
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>VIN (optional)</Text>
                  <AppTextInput
                    style={styles.input}
                    value={draft.vin}
                    onChangeText={t => setDraft(p => ({ ...p, vin: t }))}
                    placeholder="17-character VIN"
                    autoCapitalize="characters"
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Notes (optional)</Text>
                  <AppTextInput
                    style={[styles.input, styles.textArea]}
                    value={draft.notes}
                    onChangeText={t => setDraft(p => ({ ...p, notes: t }))}
                    placeholder="Any helpful info..."
                    multiline
                    numberOfLines={3}
                  />
                </View>
              </View>

              <TouchableOpacity
                style={[styles.defaultToggle, draft.isDefault && styles.defaultToggleOn]}
                onPress={() => setDraft(p => ({ ...p, isDefault: !p.isDefault }))}
                disabled={loading}
              >
                <Text style={[styles.defaultToggleText, draft.isDefault && styles.defaultToggleTextOn]}>
                  {draft.isDefault ? '✓ Default vehicle' : 'Set as default'}
                </Text>
              </TouchableOpacity>

              <View style={styles.formActions}>
                <TouchableOpacity style={styles.secondaryBtn} onPress={() => { setIsEditing(false); setDraft(emptyDraft()); }} disabled={loading}>
                  <Text style={styles.secondaryBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.primaryBtn} onPress={save} disabled={loading}>
                  <Text style={styles.primaryBtnText}>{loading ? 'Saving…' : 'Save vehicle'}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          ) : (
            <ScrollView style={styles.list} contentContainerStyle={{ paddingBottom: 16 }}>
              {loading && <Text style={styles.loadingText}>Loading…</Text>}

              {vehicles.length === 0 && !loading ? (
                <View style={styles.empty}>
                  <Text style={styles.emptyTitle}>No saved vehicles yet</Text>
                  <Text style={styles.emptyText}>Add one now to speed up future bookings.</Text>
                </View>
              ) : null}

              {vehicles.map(v => {
                const isSelected = v.id === selectedVehicleId;
                return (
                  <View key={v.id} style={[styles.vehicleRow, isSelected && styles.vehicleRowSelected]}>
                    <TouchableOpacity
                      style={styles.vehicleMain}
                      onPress={() => onSelectVehicleId(v.id)}
                      disabled={loading}
                    >
                      <Text style={styles.vehicleName}>
                        {formatVehicleLabel(v)}{v.isDefault ? ' (Default)' : ''}
                      </Text>
                      {!!v.plate && <Text style={styles.vehicleMeta}>Plate: {v.plate}</Text>}
                      {!!v.vin && <Text style={styles.vehicleMeta}>VIN: {v.vin}</Text>}
                    </TouchableOpacity>

                    <View style={styles.vehicleActions}>
                      <TouchableOpacity onPress={() => setDefault(v.id)} disabled={loading} style={styles.linkBtn}>
                        <Text style={styles.linkText}>Default</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => startEdit(v)} disabled={loading} style={styles.linkBtn}>
                        <Text style={styles.linkText}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => remove(v.id)} disabled={loading} style={styles.linkBtn}>
                        <Text style={[styles.linkText, styles.dangerText]}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}

              <View style={styles.selectionInfo}>
                <Text style={styles.selectionInfoText}>
                  Selected: {selectedVehicle ? formatVehicleLabel(selectedVehicle) : 'No vehicle'}
                </Text>
              </View>
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  card: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    maxHeight: '90%',
    paddingBottom: Platform.OS === 'ios' ? 16 : 12,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111',
  },
  closeBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  closeText: {
    fontSize: 18,
    color: '#444',
  },
  subheader: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 6,
    flexDirection: 'row',
    gap: 10,
  },
  primaryBtn: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryBtnText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
  secondaryBtn: {
    backgroundColor: '#f2f4f7',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtnText: {
    color: '#111',
    fontWeight: '700',
    fontSize: 14,
  },
  list: {
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  loadingText: {
    color: '#666',
    paddingVertical: 10,
  },
  empty: {
    paddingVertical: 22,
    alignItems: 'center',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111',
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 13,
    color: '#666',
  },
  vehicleRow: {
    borderWidth: 1,
    borderColor: '#e6e8eb',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  vehicleRowSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f7ff',
  },
  vehicleMain: {
    marginBottom: 10,
  },
  vehicleName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111',
    marginBottom: 4,
  },
  vehicleMeta: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  vehicleActions: {
    flexDirection: 'row',
    gap: 14,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  linkBtn: {
    paddingVertical: 2,
  },
  linkText: {
    color: '#007AFF',
    fontWeight: '700',
    fontSize: 13,
  },
  dangerText: {
    color: '#d92d20',
  },
  selectionInfo: {
    paddingTop: 8,
  },
  selectionInfoText: {
    color: '#444',
    fontSize: 12,
  },
  form: {
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '800',
    marginBottom: 10,
    color: '#111',
  },
  row: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  col: {
    flex: 1,
  },
  label: {
    fontSize: 12,
    color: '#444',
    marginBottom: 6,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: '#e6e8eb',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111',
    backgroundColor: '#fff',
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  defaultToggle: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: '#e6e8eb',
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  defaultToggleOn: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f7ff',
  },
  defaultToggleText: {
    color: '#111',
    fontWeight: '700',
    fontSize: 13,
  },
  defaultToggleTextOn: {
    color: '#007AFF',
  },
  formActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
    marginBottom: 8,
  },
});

export default MyVehiclesModal;

